import CryptoJS from 'crypto-js';
import { BillingTypeEunm, BusinessTypeMap, BusinessTypeEnum, SkuEnum } from '@/utils/constants';
import dayjs from 'dayjs';
/** 固定 Header 高度，滚动锚点需减去该值 */
const HEADER_HEIGHT = 100;

export const ENV = import.meta.env.VITE_ENV || "dev";
/**
 * 单个 web 资源路径 → 完整 URL
 */
export function resolveWebAsset(path) {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) {
        return path;
    }
    
    // ✨ 关键改动：把变量的获取移入函数内部！
    // 只有在函数真正被调用时才会去读取环境变量，从而彻底避开文件加载时的初始化顺序问题。
    const baseUrl = import.meta?.env?.VITE_WEB_ASSETS_URL || '';
    
    const base = baseUrl.replace(/\/$/, '');
    const file = String(path).replace(/^\//, '');
    if (!base) {
        return `/${file}`;
    }
    return `${base}/${file}`;
}

/**
 * 批量处理 webAssets 对象，给每个 value 拼上 CDN 前缀
 */
export function resolveWebAssets(assets) {
    return Object.fromEntries(
        Object.entries(assets).map(([key, value]) => [key, resolveWebAsset(value)])
    );
}


/**
 * 密码加密函数
 */
export const encryptPassword = (password) => {
    return CryptoJS.enc.Base64.stringify(CryptoJS.SHA256(password));
};

/**
 * 签名计算函数
 */
export const calSign = (path, obj = null, accessToken = null) => {
    let finalStr = "moldai" + ":" + path;
    const timestampStr = Date.now();
    finalStr += ":" + timestampStr;

    if (obj) {
        const sortedObj = Object.keys(obj).sort().reduce((acc, key) => {
            acc[key] = obj[key];
            return acc;
        }, {});
        const dataStr = JSON.stringify(sortedObj);
        finalStr += ":" + dataStr;
    }

    if (accessToken) {
        finalStr += ":" + accessToken;
    }

    const hash = CryptoJS.MD5(finalStr);
    const hashInBase64 = CryptoJS.enc.Base64.stringify(hash);
    let authorizationStr = "timestamp:" + timestampStr + "," + "sign:" + hashInBase64;

    if (accessToken) {
        authorizationStr += ",accessToken:" + accessToken;
    }

    return authorizationStr;
};



/** 将 DOM 元素滚到视口内（减去固定 Header 高度） */
export function scrollElementIntoView(element, offset = HEADER_HEIGHT) {
    if (!element) return;
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

export function scrollToTop({ behavior = 'smooth' } = {}) {
    window.scrollTo({ top: 0, left: 0, behavior });
}


export const formatNumber = (num) => {
    if (num == null) return '0';
    return num.toLocaleString();
};

// 环境判断工具函数
export const isDevEnv = () => ENV === "dev";
export const isTestEnv = () => ENV === "test"; 
export const isProdEnv = () => ENV === "prod";


// 环境配置 - 根据Vite环境变量动态获取
export const getApiBaseUrl = () => {
    // 开发环境使用环境变量，生产环境使用构建时注入的变量
    if (import.meta.env.DEV) {
      return import.meta.env.VITE_API_BASE_URL || "https://testapi.moldai.net";
    }
    return import.meta.env.VITE_API_BASE_URL || "https://testapi.moldai.net";
};



/** ISO 时间 → "YYYY-MM-DD HH:mm" */
export const formatTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};



/**
 * 根据流水类型和数据生成详细的计费规则描述
 * @param {Object} item 接口返回的单条流水记录
 * @returns {string} 构造好的详情字符串
 */
export const generateBillingDetails = (item) => {
    const type = Number(item.transaction_type);
    const info = item.info || {};
    const billingRule = info.billing_rule || {};
    const taskParam = info.task_param || {};

    // 需要显示计费公式的业务类型列表
    const formulaBusinessTypes = [
        BusinessTypeEnum.SC,
        BusinessTypeEnum.SC_EXPORT,
        BusinessTypeEnum.FORMAT_CONVERT,
        BusinessTypeEnum.DSM_DOM,
        BusinessTypeEnum.DENSE_POINTCLOUD,
        BusinessTypeEnum.MESH,
        BusinessTypeEnum.GS_SELF,
        BusinessTypeEnum.GS_GAODE,
        BusinessTypeEnum.GS_THIRD
    ];

    // 1. 计费公式类流水：扣费、冻结、返还
    if (
        type === BillingTypeEunm.DEDUCT || 
        type === BillingTypeEunm.FREEZE || 
        type === BillingTypeEunm.RELEASE
    ) {
        if (formulaBusinessTypes.includes(info.business_type)) {
            // 从 info 中安全提取字段，并提供默认值
            const basePoints = billingRule.base_points ?? 0;
            const weightFactor = billingRule.weight_factor ?? 1;
            const imageFactor = billingRule.image_factor ?? 1;
            const imageCount = taskParam.imageCount ?? 0;
            
            // 提取业务类型的中文描述
            const businessName = BusinessTypeMap[item.business_type] || '业务';
            
            // 判断是否存在成功 Tile 数
            const hasSuccessTiles = taskParam.successTiles !== undefined && taskParam.successTiles !== null;
            const successTiles = taskParam.successTiles ?? 1;

            // 按公式实际计算积分值
            let pointsResult = basePoints * weightFactor * imageFactor;
            if (hasSuccessTiles) {
                pointsResult *= successTiles;
            }

            // 动态拼接公式字符串
            let formulaStr = `基础底价${basePoints} × ${businessName}权重${weightFactor} × 照片因子${imageFactor}(${imageCount}张)`;
            if (hasSuccessTiles) {
                formulaStr += ` × 成功Tile${successTiles}`;
            }
            formulaStr += ` = ${pointsResult}积分`;

            return formulaStr;
        }else{
            let action = '';
            if (type === BillingTypeEunm.DEDUCT) action = '扣除';
            if (type === BillingTypeEunm.FREEZE) action = '冻结';
            if (type === BillingTypeEunm.RELEASE) action = '返还';
            return  `生成式积分${action}`;
        }
    }

    // 2. 非计费公式类流水：充值、过期、赠送、退款
    const nonBillingMap = {
        [BillingTypeEunm.RECHARGE]: '',
        [BillingTypeEunm.EXPIRE]: '(过期)',
        [BillingTypeEunm.GIFT]: '(套餐赠送)',
        [BillingTypeEunm.REFUND]: '(系统异常扣除退款)',
    };

    if (nonBillingMap[type] !== undefined) {
        const suffix = nonBillingMap[type];
        return `非计费公式类流水\n固定积分变动${suffix}`;
    }

    // 兜底返回
    return item.info ? JSON.stringify(item.info) : '-';
};



/**
 * 防抖函数
 * @param {Function} fn - 目标执行函数
 * @param {number} delay - 防抖延迟时间(ms)
 * @param {boolean} immediate - 是否立即执行：true=首次触发立即执行，false=最后一次触发后延迟执行
 * @returns {Function} 防抖包装后的函数
 */
export const debounce = (fn, delay, immediate = false) => {
    let timer = null;
  
    return function (...args) {
      // 清空上一次定时器
      if (timer) clearTimeout(timer);
  
      // 立即执行模式：仅首次触发执行，后续等待冷却
      if (immediate && !timer) {
        fn.apply(this, args);
      }
  
      timer = setTimeout(() => {
        // 非立即执行：延迟后执行函数
        if (!immediate) {
          fn.apply(this, args);
        }
        timer = null;
      }, delay);
    };
};


// 获取今天的时间  23：59
export const getDaysAgo = (days = 30) => {
    return dayjs().subtract(days, 'day').startOf('day').toISOString();
};

// 获取今天结束时间 23:59:59
export const getTodayEnd = () => {
    return dayjs().endOf('day').toISOString();
};

// sku 编号 → 中文标签映射
const SkuLabelMap = {
    [SkuEnum.YEAR]: '年付',
    [SkuEnum.MONTH]: '月付',
    [SkuEnum.DAY]: '日付',
};

/**
 * 根据产品名和 sku 拼接购买内容展示字符串
 * @param {string} productName - 产品名称
 * @param {number|null} sku - SKU 枚举值
 * @returns {string} 如 "专业版 年付订阅" 或 "专业版"
 */
export const formatProductName = (productName, sku) => {
    if (sku != null && Object.values(SkuEnum).includes(sku)) {
        return `${productName} ${SkuLabelMap[sku]}订阅`;
    }
    return productName;
};

/**
 * 密码校验规则：长度 8-14 位，全为 ASCII 可见字符（字母、数字、英文标点），
 * 且字母/数字/标点三类中至少包含两类；不支持空格、中文、emoji 及其他符号。
 * @param {string} pw - 待校验的密码
 * @returns {{ ok: boolean, msg: string }} 校验结果与对应的提示语（ok=false 时 msg 即失败原因）
 */
export const validatePassword = (pw) => {
    if (typeof pw !== "string" || pw.length === 0) {
        return { ok: false, msg: "请输入密码" };
    }
    if (pw.length < 8) {
        return { ok: false, msg: `密码至少 8 位（还差 ${8 - pw.length} 位）` };
    }
    if (pw.length > 14) {
        return { ok: false, msg: "密码不能超过 14 位" };
    }
    if (/\s/.test(pw)) {
        return { ok: false, msg: "密码不能包含空格" };
    }

    // 字符类别判断
    const isLetter = (c) => /[A-Za-z]/.test(c);
    const isDigit = (c) => /[0-9]/.test(c);
    // 英文标点：ASCII 可见字符中，既不是字母也不是数字的部分
    const isPunct = (c) => /^[\x21-\x7E]$/.test(c) && !isLetter(c) && !isDigit(c);

    let hasLetter = false, hasDigit = false, hasPunct = false;
    for (const ch of pw) {
        if (isLetter(ch)) hasLetter = true;
        else if (isDigit(ch)) hasDigit = true;
        else if (isPunct(ch)) hasPunct = true;
        else {
            return { ok: false, msg: "包含不支持的字符（如中文、emoji 或控制符）" };
        }
    }

    const kinds = [hasLetter, hasDigit, hasPunct].filter(Boolean).length;
    if (kinds < 2) {
        return { ok: false, msg: "字母、数字、标点中至少包含两类" };
    }

    return { ok: true, msg: "✓ 密码格式正确" };
};